import { GetServerSideProps, NextPage } from 'next'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import { getMemberByUserName, getMembersUserNameFollowingsV1 } from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import {
  FETCH_COUNT_PER_PAGE,
  MemberFollowList,
  Props,
} from '~/features/MemberFollowList'
import { useMemberFollowings } from '~/features/MemberFollowList/hooks/useMemberFollwings'
import { getMetadata } from '~/features/MemberFollowList/lib/getMetadata'
import { useMember } from '~/hooks/useMember'
import { MEMBER_DETAIL_HEADER_ADJUST_URL } from '~/lib/constants'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'

const MemberFollowListPage: NextPage<Props> = ({ userName, pageNumber }) => {
  const member = useMember(userName)
  const memberFollowings = useMemberFollowings(userName, pageNumber)
  if (member === undefined || memberFollowings === undefined) return null
  const metadata = getMetadata(member, memberFollowings.total_count)
  return (
    <Layout
      breadcrumbList={[
        member.shop
          ? {
              name: member.shop.name,
              path: `/shop/${member.shop.id}/`,
              disableTranslation: true,
            }
          : [],
        {
          name: member.nick_name,
          path: `/${member.user_name}/`,
          disableTranslation: true,
        },
        { name: 'フォロー一覧' },
      ].flat()}
      headerProps={{ appLink: metadata.appUrl }}
      appDownloadUrl={MEMBER_DETAIL_HEADER_ADJUST_URL}
      abstractUrl="wear.jp/username/follow/"
    >
      <Meta {...metadata} />
      <MemberFollowList userName={userName} pageNumber={pageNumber} />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const userName = query.username as string
  const { pageNumber, isValid } = getPageNumberFromQueryValue(query.pageno)
  if (!isValid) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/follower/`,
      },
    }
  }
  const [memberRes, followingsRes] = await Promise.all([
    getMemberByUserName(client, {
      parameter: { user_name: userName },
    }),
    getMembersUserNameFollowingsV1(client, {
      parameter: {
        user_name: userName,
        pageno: pageNumber,
        pagesize: FETCH_COUNT_PER_PAGE,
      },
    }),
  ])
  if (memberRes.type === 'error') {
    if (memberRes.raw.status === 404) return { notFound: true }
    throw new Error('Failed to fetch member')
  }
  if (followingsRes.type === 'error') {
    if (followingsRes.raw.status === 404) return { notFound: true }
    throw new Error('Failed to fetch followings')
  }
  return {
    props: {
      fallback: {
        [unstable_serialize(['getMemberByUserName', userName])]: memberRes.data,
        [unstable_serialize([
          'getMembersUserNameFollowingsV1',
          userName,
          pageNumber,
        ])]: followingsRes.data,
      },
      userName,
      pageNumber,
    },
  }
}

export default MemberFollowListPage
